import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, MapPin, Car, User, MessageSquare, Phone, Mail, CheckCircle2, ArrowRight, Plus, ShoppingBag, Check, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { packages, addOns, businessInfo } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingForm = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '',
    vehicleType: '', vehicleMake: '', vehicleModel: '',
    address: '', preferredDate: '', preferredTime: '',
    selectedPackage: '', specialRequests: ''
  });
  const [selectedAddOnIds, setSelectedAddOnIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [quoteEstimate, setQuoteEstimate] = useState(null);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  // Apply a quote prefill pushed by other sections (e.g. Pre-Sale pitch CTA)
  useEffect(() => {
    const apply = () => {
      const raw = sessionStorage.getItem('quotePrefill');
      if (!raw) return;
      try {
        const prefill = JSON.parse(raw);
        setFormData(prev => ({
          ...prev,
          selectedPackage: prefill.package || prev.selectedPackage,
          selectedAddOns: Array.isArray(prefill.addons) ? prefill.addons.map(String) : prev.selectedAddOns,
        }));
        sessionStorage.removeItem('quotePrefill');
        if (prefill.package) toast.success(`Pre-filled with ${prefill.package}`);
      } catch { /* ignore bad prefill */ }
    };
    window.addEventListener('quotePrefillUpdated', apply);
    return () => window.removeEventListener('quotePrefillUpdated', apply);
  }, []);

  // Maintenance Detail is locked — only bookable by customers unlocked after a Premium Detail
  const handlePackageChange = async (value) => {
    if (value === 'Maintenance Detail') {
      if (!formData.email && !formData.phone) {
        toast.error('Enter your email & phone above so we can check your maintenance access');
        return;
      }
      try {
        const res = await axios.get(`${API}/maintenance-status`, { params: { email: formData.email, phone: formData.phone } });
        if (!res.data.unlocked) {
          toast.error('Maintenance Detail unlocks after your first Premium Detail with us. Book the Premium Detail first!');
          return;
        }
        toast.success('Maintenance access confirmed — welcome back!');
      } catch (err) {
        toast.error('Could not verify access. Please call us to book Maintenance.');
        return;
      }
    }
    handleChange('selectedPackage', value);
  };

  const toggleAddOn = (id) => {
    setSelectedAddOnIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectedAddOnObjects = addOns.filter(a => selectedAddOnIds.includes(a.id));
  const addOnTotal = Math.round(selectedAddOnObjects.reduce((sum, a) => sum + (a.priceValue || 0), 0) * 100) / 100;
  const hasQuoteOnly = selectedAddOnObjects.some(a => a.priceValue === null);

  // Listen for quote pre-fill events
  React.useEffect(() => {
    const applyPrefill = () => {
      try {
        const raw = sessionStorage.getItem('quotePrefill');
        if (!raw) return;
        const data = JSON.parse(raw);
        setFormData(prev => ({
          ...prev,
          selectedPackage: data.package || prev.selectedPackage,
        }));
        // pre-check add-ons from quote calculator
        if (data.addons?.length) {
          const ids = addOns.filter(a => data.addons.includes(a.name)).map(a => a.id);
          setSelectedAddOnIds(ids);
        }
        setQuoteEstimate(data);
        sessionStorage.removeItem('quotePrefill');
      } catch (e) {
        console.error('Prefill error', e);
      }
    };
    applyPrefill();
    window.addEventListener('quote-prefill', applyPrefill);
    return () => window.removeEventListener('quote-prefill', applyPrefill);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.selectedPackage || !formData.address) {
      toast.error('Please fill in all required fields');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...formData,
        selectedAddOns: selectedAddOnObjects.map(a => a.name),
      });
      toast.success("Booking received! We'll be in touch within 24 hours.");
      setIsSubmitted(true);
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong. Please call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="booking" className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-3xl shadow-2xl p-12 border border-slate-100">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4">Booking Received!</h2>
            <p className="text-lg text-slate-600 mb-8">
              Thanks {formData.name.split(' ')[0]}! We've received your request for the <strong>{formData.selectedPackage}</strong> package.
              We'll call or text you within 24 hours to confirm.
            </p>
            <div className="bg-slate-50 rounded-2xl p-5 mb-8 text-left">
              <div className="text-xs uppercase text-slate-500 font-bold tracking-wider mb-2">Need it sooner?</div>
              <a href={`tel:${businessInfo.phone.replace(/\s/g, '')}`} className="text-2xl font-extrabold text-slate-900 flex items-center gap-2">
                <Phone className="w-5 h-5 text-blue-600" />
                {businessInfo.phone}
              </a>
            </div>
            <Button
              data-testid="new-booking-btn"
              onClick={() => { setIsSubmitted(false); setFormData({ name: '', email: '', phone: '', vehicleType: '', vehicleMake: '', vehicleModel: '', address: '', preferredDate: '', preferredTime: '', selectedPackage: '', specialRequests: '' }); }}
              variant="outline"
              className="rounded-xl"
            >
              Book Another Detail
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="booking" className="py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-40 -translate-x-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-100 rounded-full blur-3xl opacity-40 translate-x-1/2"></div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <div className="inline-flex items-center px-4 py-1.5 bg-blue-50 border border-blue-100 rounded-full mb-6">
            <Calendar className="w-4 h-4 text-blue-600 mr-2" />
            <span className="text-sm font-semibold text-blue-700">Book Online</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6 leading-tight">
            Book in <span className="text-blue-600">60 seconds.</span>
          </h2>
          <p className="text-lg sm:text-xl text-slate-600">
            Fill in the details below — we'll confirm your booking within 24 hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2 bg-white rounded-3xl shadow-2xl shadow-slate-900/5 p-8 md:p-10 border border-slate-100">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Personal */}
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Your details</h3>
                </div>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-sm font-semibold text-slate-700">Full Name *</Label>
                      <Input data-testid="form-name" id="name" placeholder="John Smith" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} required className="mt-1.5 rounded-xl h-12 border-slate-200" />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm font-semibold text-slate-700">Phone *</Label>
                      <Input data-testid="form-phone" id="phone" type="tel" placeholder="0412 345 678" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} required className="mt-1.5 rounded-xl h-12 border-slate-200" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm font-semibold text-slate-700">Email *</Label>
                    <Input data-testid="form-email" id="email" type="email" placeholder="john@example.com" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} required className="mt-1.5 rounded-xl h-12 border-slate-200" />
                  </div>
                </div>
              </div>

              {/* Vehicle */}
              <div className="pt-6 border-t border-slate-100">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Car className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Your vehicle</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700">Vehicle Type *</Label>
                    <Select value={formData.vehicleType} onValueChange={(v) => handleChange('vehicleType', v)}>
                      <SelectTrigger data-testid="form-vtype" className="mt-1.5 rounded-xl h-12 border-slate-200">
                        <SelectValue placeholder="Select vehicle type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sedan">Sedan</SelectItem>
                        <SelectItem value="suv">SUV</SelectItem>
                        <SelectItem value="hatchback">Hatchback</SelectItem>
                        <SelectItem value="wagon">Wagon</SelectItem>
                        <SelectItem value="ute">Ute / Pickup</SelectItem>
                        <SelectItem value="van">Van</SelectItem>
                        <SelectItem value="coupe">Coupe / Sports</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="vmake" className="text-sm font-semibold text-slate-700">Make</Label>
                      <Input data-testid="form-vmake" id="vmake" placeholder="e.g., Toyota" value={formData.vehicleMake} onChange={(e) => handleChange('vehicleMake', e.target.value)} className="mt-1.5 rounded-xl h-12 border-slate-200" />
                    </div>
                    <div>
                      <Label htmlFor="vmodel" className="text-sm font-semibold text-slate-700">Model</Label>
                      <Input data-testid="form-vmodel" id="vmodel" placeholder="e.g., Camry" value={formData.vehicleModel} onChange={(e) => handleChange('vehicleModel', e.target.value)} className="mt-1.5 rounded-xl h-12 border-slate-200" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Service */}
              <div className="pt-6 border-t border-slate-100">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Service details</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700">Choose Package *</Label>
                    <Select value={formData.selectedPackage} onValueChange={handlePackageChange}>
                      <SelectTrigger data-testid="form-package" className="mt-1.5 rounded-xl h-12 border-slate-200">
                        <SelectValue placeholder="Select a detailing package" />
                      </SelectTrigger>
                      <SelectContent>
                        {packages.map(pkg => (
                          <SelectItem key={pkg.id} value={pkg.name} data-testid={`form-package-option-${pkg.id}`}>
                            {pkg.name} — {pkg.startingPrice}{pkg.locked ? ' (Premium customers only)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
                      <Lock className="w-3 h-3 text-amber-500" />
                      Maintenance Detail is unlocked for customers who have had a Premium Detail.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="address" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      Service Address *
                    </Label>
                    <Input data-testid="form-address" id="address" placeholder="123 Main St, Sydney NSW 2000" value={formData.address} onChange={(e) => handleChange('address', e.target.value)} required className="mt-1.5 rounded-xl h-12 border-slate-200" />
                    <p className="text-xs text-slate-500 mt-1.5">Where should we detail your car?</p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pdate" className="text-sm font-semibold text-slate-700">Preferred Date</Label>
                      <Input data-testid="form-date" id="pdate" type="date" value={formData.preferredDate} onChange={(e) => handleChange('preferredDate', e.target.value)} min={new Date().toISOString().split('T')[0]} className="mt-1.5 rounded-xl h-12 border-slate-200" />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-slate-700">Preferred Time</Label>
                      <Select value={formData.preferredTime} onValueChange={(v) => handleChange('preferredTime', v)}>
                        <SelectTrigger data-testid="form-time" className="mt-1.5 rounded-xl h-12 border-slate-200">
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="morning">Morning (8am – 12pm)</SelectItem>
                          <SelectItem value="afternoon">Afternoon (12pm – 4pm)</SelectItem>
                          <SelectItem value="evening">Evening (4pm – 7pm)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Add-ons */}
              <div className="pt-6 border-t border-slate-100">
                <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Plus className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900">
                      Add-ons <span className="text-sm font-normal text-slate-400">(optional)</span>
                    </h3>
                  </div>
                  {selectedAddOnIds.length > 0 && (
                    <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-sm font-bold">
                      <ShoppingBag className="w-4 h-4" />
                      {selectedAddOnIds.length} added
                      {!hasQuoteOnly && addOnTotal > 0 && ` • +$${addOnTotal}`}
                    </div>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {addOns.map((a) => {
                    const active = selectedAddOnIds.includes(a.id);
                    return (
                      <button
                        key={a.id}
                        type="button"
                        data-testid={`form-addon-${a.id}`}
                        onClick={() => toggleAddOn(a.id)}
                        className={`text-left p-4 rounded-2xl border-2 transition-all duration-200 flex items-start gap-3 ${
                          active
                            ? 'border-blue-500 bg-blue-50 shadow-md shadow-blue-100'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded-md flex-shrink-0 flex items-center justify-center mt-0.5 transition-colors ${
                          active ? 'bg-blue-600' : 'bg-white border-2 border-slate-300'
                        }`}>
                          {active && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2 mb-1">
                            <span className="font-bold text-slate-900 text-sm">{a.name}</span>
                            <span className="text-sm font-extrabold text-blue-600 flex-shrink-0">{a.price}</span>
                          </div>
                          <div className="text-xs text-slate-500 leading-snug">{a.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {selectedAddOnIds.length > 0 && (
                  <div className="mt-5 bg-gradient-to-br from-slate-900 to-blue-950 rounded-2xl p-5 text-white">
                    <div className="flex items-center gap-2 mb-3">
                      <ShoppingBag className="w-5 h-5 text-cyan-300" />
                      <span className="font-bold text-sm uppercase tracking-wider text-cyan-300">Your basket</span>
                    </div>
                    <ul className="space-y-2 mb-3">
                      {selectedAddOnObjects.map(a => (
                        <li key={a.id} className="flex justify-between text-sm">
                          <span className="text-slate-200">{a.name}</span>
                          <span className="font-bold">{a.price}</span>
                        </li>
                      ))}
                    </ul>
                    {!hasQuoteOnly && addOnTotal > 0 && (
                      <div className="pt-3 border-t border-white/10 flex justify-between items-center">
                        <span className="text-sm text-slate-300">Add-ons subtotal</span>
                        <span className="text-xl font-extrabold text-cyan-300">${addOnTotal}+</span>
                      </div>
                    )}
                    <p className="text-xs text-slate-400 mt-2">Added on top of your selected package price.</p>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="pt-6 border-t border-slate-100">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Anything else?</h3>
                </div>
                <Textarea data-testid="form-notes" placeholder="Add-ons needed, pet hair, deep stains, specific concerns..." value={formData.specialRequests} onChange={(e) => handleChange('specialRequests', e.target.value)} rows={4} className="rounded-xl border-slate-200 resize-none" />
              </div>

              <Button data-testid="submit-booking-btn" type="submit" disabled={isSubmitting} className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-xl py-7 text-base font-bold shadow-xl shadow-blue-600/25 group">
                {isSubmitting ? 'Sending...' : (<>Submit Booking Request<ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" /></>)}
              </Button>
              <p className="text-xs text-slate-500 text-center">We'll call or text you within 24 hours to confirm and provide a final quote.</p>
            </form>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-slate-900 to-blue-950 rounded-3xl p-7 text-white shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 bg-blue-500 rounded-full blur-3xl opacity-20"></div>
              <div className="relative">
                <h3 className="text-xl font-bold mb-2">Prefer to call?</h3>
                <p className="text-slate-300 text-sm mb-5">Speak to us directly for instant quotes.</p>
                <a href={`tel:${businessInfo.phone.replace(/\s/g, '')}`} className="block bg-white text-slate-900 rounded-xl py-4 px-5 font-bold text-lg text-center hover:bg-slate-100 transition-colors">
                  <Phone className="w-5 h-5 inline mr-2 -mt-1" />
                  {businessInfo.phone}
                </a>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-6 border border-slate-100">
              <h3 className="font-bold text-slate-900 mb-4">What happens next?</h3>
              <div className="space-y-3">
                {['You submit your request', 'We call you within 24h', 'We confirm date & quote', 'We arrive & detail your car'].map((step, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 font-bold text-sm flex items-center justify-center flex-shrink-0">{i + 1}</div>
                    <span className="text-slate-700 text-sm pt-0.5">{step}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-blue-50 rounded-3xl p-6 border border-blue-100">
              <Mail className="w-6 h-6 text-blue-600 mb-2" />
              <div className="text-xs uppercase text-blue-700 font-bold tracking-wider mb-1">Email Us</div>
              <a href={`mailto:${businessInfo.email}`} className="text-slate-900 font-bold hover:text-blue-600 transition-colors break-all">{businessInfo.email}</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingForm;
